import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { TShiftSchema, ShiftSchema } from "../Models";
import { Button } from "@/components/ui/button";
import { Dispatch, SetStateAction, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { Shift, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { onInvalid } from "@/utils/form";
import ShiftFormInputs from "./Shared/ShiftFormInputs";
import { CircleLoader } from "@/components/shared/loaders";

interface Props {
  showEditShiftModal: boolean;
  setShowEditShiftModal: Dispatch<SetStateAction<boolean>>;
  tenderDuration: TenderDurationFragment;
  selectedShift: Shift;
  isNewService: boolean;
  onEditShift: (_shift: TShiftSchema, _selectedShift: Shift, _setShowEditShiftModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  isProcessing?: boolean;
};

function EditShiftComponent(props: Props) {
  const {
    showEditShiftModal,
    setShowEditShiftModal,
    tenderDuration,
    selectedShift,
    isNewService,
    onEditShift,
    isProcessing
  } = props;

  const [useOptionalDates, setUseOptionalDates] = useState(!!(selectedShift.startDate || selectedShift.endDate));

  // Initialize the form
  const form = useForm<TShiftSchema>({
    resolver: zodResolver(ShiftSchema) as Resolver<TShiftSchema>,
    defaultValues: {
      startTime: selectedShift.startTime,
      endTime: selectedShift.endTime,
      pauseStartTime: selectedShift.pauseStartTime || "",
      pauseEndTime: selectedShift.pauseEndTime || "",
      employeeQuantity: selectedShift.employeeQuantity,
      subContractorQuantity: 0,
      monday: selectedShift.monday,
      tuesday: selectedShift.tuesday,
      wednesday: selectedShift.wednesday,
      thursday: selectedShift.thursday,
      friday: selectedShift.friday,
      saturday: selectedShift.saturday,
      sunday: selectedShift.sunday,
      holiday: selectedShift.holiday,
      applyHolidayPay: selectedShift.applyHolidayPay,
      startDate: selectedShift.startDate ? new Date(selectedShift.startDate) : undefined,
      endDate: selectedShift.endDate ? new Date(selectedShift.endDate) : undefined
    },
    mode: "onChange",
  });

  // Hooks
  const { t } = useTranslation(["modals", "common", "translation"]);

  /**
   * Handler function for editing a shift
   * Works by removing the old shift and adding a new one with the edited values
   * @param data - The data to edit the shift with
   */
  const onSubmit: SubmitHandler<TShiftSchema> = async (
    data: TShiftSchema
  ) => {
    if (isNewService) {
      // If the service is new, we need to edit the shift in the shifts array from the parent component
      await onEditShift(data, selectedShift, setShowEditShiftModal);
    } else {
      // Create a new object with the modified dates
      const modifiedData = {
        ...data,
        startDate: useOptionalDates ? data.startDate : undefined,
        endDate: useOptionalDates ? data.endDate : undefined
      };

      await onEditShift(modifiedData, selectedShift, setShowEditShiftModal);
    }
  };

  return (
    <Dialog open={showEditShiftModal} onOpenChange={setShowEditShiftModal}>
      <DialogContent className="select-none min-w-fit max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="edit-shift-form">
          <DialogHeader className="text-left">
            <DialogTitle>{t("shift.edittitle")}</DialogTitle>
            <DialogDescription>{t("shift.editdescription")}</DialogDescription>
          </DialogHeader>
          {/* form inputs */}
          <ShiftFormInputs
            form={form}
            tenderDuration={tenderDuration}
            setUseOptionalDates={setUseOptionalDates}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isProcessing}>
                {t("common:buttons.cancel")}
              </Button>
            </DialogClose>
            <Button variant="default" type="submit" disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.edit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditShiftComponent;
